import { create } from 'zustand';
import * as THREE from 'three';
import { v4 as uuidv4 } from 'uuid';
import type { SketchSolid, SketchGuide, SketchDisplayStyle, SketchGroup, SketchComponentDef, SketchFace, SketchVec3, SketchLooseEdge, SketchTag, SketchScene, SketchShadowSettings, SketchDimension, SketchLabel, SketchDrawingAxes } from '../core/sketch/types';
import { parseFaceSelectionId, parseEdgeSelectionId, parseLooseEdgeSelectionId } from '../core/sketch/types';
import { detectClosedRegions } from '../core/sketch/edgeGraph';
import { facesOfEdges } from '../core/sketch/topology';
import { transformSolid, copySolid } from '../core/sketch/transform';

/**
 * Sketch workbench store — the freeform modeling document.
 *
 * Mirrors the app-store's HistoryAction undo/redo pattern (every user-visible
 * mutation commits one atomic, labeled action). Solids are immutable records:
 * tools hand complete replacements to updateSolid and never mutate in place,
 * which keeps history snapshots trivial (per-solid before/after pairs).
 *
 * Persistence contract (Phase: .yap envelope): the document serializer reads
 * { solids, guides } via sketchDocumentSnapshot(); hydrateSketchDocument()
 * restores them on load. Selection/hover/VCB are session-only state.
 */

export interface HistoryAction {
    label: string;
    undo: () => void;
    redo: () => void;
}

export type SketchSelectionId = string; // solid id, 'solidId:faceId' composite, 'guide:xxx'

/** Face ref returned when a drawn segment closes a region into a new face. */
export interface FormedFaceRef { solidId: string; faceId: string }

interface SketchState {
    solids: Record<string, SketchSolid>;
    /** Loose edges: drawn segments not yet absorbed into a face. */
    edges: Record<string, SketchLooseEdge>;
    guides: Record<string, SketchGuide>;
    /** Named group containers (Outliner tree; solid.groupId references these). */
    groups: Record<string, SketchGroup>;
    /** Reusable component definitions (library panel). */
    componentDefs: Record<string, SketchComponentDef>;
    /** Tags (Sketch layers): named visibility switches over solids. */
    tags: Record<string, SketchTag>;
    /** Saved scenes: named camera + style snapshots (Scenes panel). */
    scenes: Record<string, SketchScene>;
    /** Shadow settings (Shadows panel; sun position + light levels). */
    shadows: SketchShadowSettings;
    /** Dimension annotations (Dimension tool). */
    dimensions: Record<string, SketchDimension>;
    /** Text label annotations (Text tool). */
    labels: Record<string, SketchLabel>;
    /** Custom drawing axes (Axes tool); null = the Z=0 world ground. */
    drawingAxes: SketchDrawingAxes | null;
    selectedIds: SketchSelectionId[];
    hoveredId: SketchSelectionId | null;
    /** Measurements box (VCB) text; tools read it on Enter, not per keystroke. */
    vcbText: string;
    displayStyle: SketchDisplayStyle;
    showGuides: boolean;
    /** Paint Bucket target material (palette id from SKETCH_MATERIALS). */
    selectedMaterialId: string;
    /** Active section plane (Section Plane tool); null = no section. */
    sectionPlane: { normal: [number, number, number]; constant: number } | null;
    /** True while the section-plane entity itself is the viewport selection
     *  (picked through its skin in Select mode) — exclusive with every other
     *  selection; anchors the gumball manipulator onto the plane. */
    sectionPlaneSelected: boolean;
    /** Clipping box (right-click "Create Clipping Box"); null = none. */
    sectionBox: { min: [number, number, number]; max: [number, number, number] } | null;

    history: HistoryAction[];
    historyIndex: number;

    // --- document mutations (history-committing) ---
    addSolid: (solid: SketchSolid, label?: string) => void;
    updateSolid: (id: string, next: SketchSolid, label: string) => void;
    /** Update several solids in ONE history action (multi-solid booleans). */
    updateSolids: (updates: Array<{ id: string; next: SketchSolid }>, label: string) => void;
    removeSolids: (ids: string[], label: string) => void;
    /**
     * Erase the current selection in ONE history action: composite
     * `solidId:faceId` entries drop those faces (leaving the open shell —
     * the Sketch hole workflow), plain solid ids drop whole solids, and a
     * solid left without any face is removed entirely. Returns true when
     * something was erased (the caller clears up otherwise).
     */
    deleteSelected: (label?: string) => boolean;
    /**
     * Commit one drawn segment as a loose edge on `plane`. When the segment
     * closes a coplanar region among the loose edges, the region becomes a
     * face solid and its bounding edges are consumed — all inside ONE
     * history action. Returns the formed face ref, else null.
     */
    addLooseSegment: (a: SketchVec3, b: SketchVec3, plane: THREE.Plane, label?: string) => FormedFaceRef | null;
    /** Erase loose edges (Erase tool on an edge pick). */
    removeLooseEdges: (ids: string[], label?: string) => void;
    /** Rigid transform of loose edges (cross-workbench edit router, B4). */
    transformLooseEdges: (ids: string[], matrix: THREE.Matrix4, label?: string) => void;
    /** Stretch the given endpoints of loose edges by `delta` in ONE history
     *  action (junction move: only the welded endpoint follows, the far end
     *  stays — the edge elongates instead of riding along). */
    stretchLooseEdges: (items: Array<{ id: string; ends: Array<'a' | 'b'> }>, delta: THREE.Vector3, label?: string) => void;
    /** Duplicate solids with fresh ids through `matrix` (cross-workbench COPY, B4). Returns the clone ids. */
    copySolids: (ids: string[], matrix: THREE.Matrix4, label?: string) => string[];
    /** Duplicate loose edges with fresh ids through `matrix` (cross-workbench COPY, B4). Returns the clone ids. */
    copyLooseEdges: (ids: string[], matrix: THREE.Matrix4, label?: string) => string[];
    /** Insert pasted solids with fresh ids through `matrix` (cross-workbench clipboard, B4). Returns the new ids. */
    insertSolids: (solids: SketchSolid[], matrix: THREE.Matrix4, label?: string) => string[];
    /** Insert pasted loose edges with fresh ids through `matrix` (cross-workbench clipboard, B4). Returns the new ids. */
    insertLooseEdges: (edges: SketchLooseEdge[], matrix: THREE.Matrix4, label?: string) => string[];
    addGuide: (guide: SketchGuide, label?: string) => void;
    removeGuides: (ids: string[], label?: string) => void;
    setSolidMaterial: (solidIds: string[], faceIds: string[], materialId: string, label?: string) => void;
    /** Hide/Unhide (Eraser's Shift variant, right-click Unhide): history action. */
    setSolidsVisible: (ids: string[], visible: boolean, label?: string) => void;
    /** Commit a Dimension annotation; returns its id. */
    addDimension: (dim: Omit<SketchDimension, 'id'>, label?: string) => string;
    removeDimensions: (ids: string[], label?: string) => void;
    /** Translate whole dimensions (a, b and the dim line offset) by `delta`. */
    moveDimensions: (ids: string[], delta: THREE.Vector3, label?: string) => void;
    /** Rigid/matrix transform of whole dimensions (a, b and offset together)
     *  — the cross-workbench edit router's entry point (gumball, MOVE, ...). */
    transformDimensions: (ids: string[], matrix: THREE.Matrix4, label?: string) => void;
    /** Commit a Text label annotation; returns its id. */
    addLabel: (label: Omit<SketchLabel, 'id'>, label2?: string) => string;
    removeLabels: (ids: string[], label?: string) => void;
    /** Translate whole labels (anchor + leader together) by `delta`. */
    moveLabels: (ids: string[], delta: THREE.Vector3, label?: string) => void;
    /** Rigid/matrix transform of whole labels (anchor + leader together)
     *  — the cross-workbench edit router's entry point. */
    transformLabels: (ids: string[], matrix: THREE.Matrix4, label?: string) => void;
    /** Place the drawing axes (Axes / UCS tools); null restores the world
     *  ground. The label names the history entry (default "Place Axes"). */
    setDrawingAxes: (axes: SketchDrawingAxes | null, label?: string) => void;

    // --- session state ---
    select: (ids: SketchSelectionId[]) => void;
    setHovered: (id: SketchSelectionId | null) => void;
    setVcbText: (text: string) => void;
    setDisplayStyle: (style: SketchDisplayStyle) => void;
    toggleGuides: () => void;
    setSelectedMaterial: (materialId: string) => void;
    setSectionPlane: (plane: { normal: [number, number, number]; constant: number } | null) => void;
    /** Select/deselect the section-plane entity (viewport skin pick). */
    setSectionPlaneSelected: (on: boolean) => void;
    /** Session setter for the clipping box (context menu; not undoable). */
    setSectionBox: (box: { min: [number, number, number]; max: [number, number, number] } | null) => void;

    // --- groups & components ---
    /** Group the given solids under one new named container (history action). */
    groupSolids: (solidIds: string[], name?: string) => string | null;
    /** Remove the group membership of the given solids (empty groups die). */
    ungroupSolids: (solidIds: string[]) => void;
    /** Save the given solids as a reusable component definition. */
    saveAsComponent: (solidIds: string[], name?: string) => string | null;
    /** Clone a component definition into the document; returns the new solid id. */
    insertComponent: (defId: string, at?: [number, number, number]) => string | null;
    /** Remove a component definition (instances stay). */
    deleteComponentDef: (defId: string) => void;
    /** Rename a group. */
    renameGroup: (groupId: string, name: string) => void;
    /** Toggle visibility of every solid in a group. */
    toggleGroupVisibility: (groupId: string) => void;

    // --- tags, scenes, shadows (panels) ---
    /** Create a tag; returns its id. History-committed. */
    addTag: (name?: string) => string;
    /** Delete a tag; member solids become untagged. History-committed. */
    removeTag: (tagId: string) => void;
    /** Rename a tag (session edit, like group renames). */
    renameTag: (tagId: string, name: string) => void;
    /** Toggle a tag's visibility (affects every member solid). History-committed. */
    setTagVisibility: (tagId: string, visible: boolean) => void;
    /** Assign (or clear, null) a tag on the given solids. History-committed. */
    assignTag: (solidIds: string[], tagId: string | null) => void;
    /** Save a scene snapshot from the CURRENT app camera + display style. */
    addScene: (camera: SketchScene['camera'], displayStyle: SketchDisplayStyle, name?: string) => string;
    /** Re-capture a scene's camera/style from the current view. */
    updateScene: (id: string, camera: SketchScene['camera'], displayStyle: SketchDisplayStyle) => void;
    /** Rename a scene. */
    renameScene: (id: string, name: string) => void;
    /** Delete a scene. */
    removeScene: (id: string) => void;
    /** Patch shadow settings (session-style model info, not undoable). */
    setShadows: (updates: Partial<SketchShadowSettings>) => void;

    // --- history engine ---
    undo: () => void;
    redo: () => void;
    pushHistory: (action: HistoryAction) => void;

    // --- document lifecycle ---
    clearDocument: () => void;
    hydrateSketchDocument: (payload: {
        solids?: SketchSolid[];
        edges?: SketchLooseEdge[];
        guides?: SketchGuide[];
        groups?: SketchGroup[];
        componentDefs?: SketchComponentDef[];
        tags?: SketchTag[];
        scenes?: SketchScene[];
        shadows?: Partial<SketchShadowSettings>;
        dimensions?: SketchDimension[];
        labels?: SketchLabel[];
        drawingAxes?: SketchDrawingAxes | null;
    }) => void;
    sketchDocumentSnapshot: () => {
        solids: SketchSolid[];
        edges: SketchLooseEdge[];
        guides: SketchGuide[];
        groups: SketchGroup[];
        componentDefs: SketchComponentDef[];
        tags: SketchTag[];
        scenes: SketchScene[];
        shadows: SketchShadowSettings;
        dimensions: SketchDimension[];
        labels: SketchLabel[];
        drawingAxes: SketchDrawingAxes | null;
    };
}

const HISTORY_LIMIT = 100;

const DEFAULT_SHADOWS: SketchShadowSettings = {
    enabled: false,
    day: 172, // ~June 21, the long-shadow summer reference day
    hour: 12,
    light: 0.8,
    dark: 0.55,
};

export const useSketchStore = create<SketchState>((set, get) => ({
    solids: {},
    edges: {},
    guides: {},
    groups: {},
    componentDefs: {},
    tags: {},
    scenes: {},
    shadows: { ...DEFAULT_SHADOWS },
    dimensions: {},
    labels: {},
    drawingAxes: null,
    selectedIds: [],
    hoveredId: null,
    vcbText: '',
    displayStyle: 'shaded',
    showGuides: true,
    selectedMaterialId: 'concrete',
    sectionPlane: null,
    sectionPlaneSelected: false,
    sectionBox: null,

    history: [],
    historyIndex: -1,

    addSolid: (solid, label) => {
        const action: HistoryAction = {
            label: label ?? `Add ${solid.name}`,
            undo: () => set((s) => {
                const solids = { ...s.solids };
                delete solids[solid.id];
                return { solids };
            }),
            redo: () => set((s) => ({ solids: { ...s.solids, [solid.id]: solid } })),
        };
        action.redo();
        get().pushHistory(action);
    },

    updateSolid: (id, next, label) => {
        const before = get().solids[id];
        if (!before) return;
        const action: HistoryAction = {
            label,
            undo: () => set((s) => ({ solids: { ...s.solids, [id]: before } })),
            redo: () => set((s) => ({ solids: { ...s.solids, [id]: next } })),
        };
        action.redo();
        get().pushHistory(action);
    },

    updateSolids: (updates, label) => {
        const before = get().solids;
        const applied = updates.filter((u) => before[u.id]);
        if (applied.length === 0) return;
        const ids = applied.map((u) => u.id);
        const nextById = Object.fromEntries(applied.map((u) => [u.id, u.next]));
        const action: HistoryAction = {
            label,
            undo: () => set((s) => ({
                solids: {
                    ...s.solids,
                    ...Object.fromEntries(ids.map((id) => [id, before[id]])),
                },
            })),
            redo: () => set((s) => ({ solids: { ...s.solids, ...nextById } })),
        };
        action.redo();
        get().pushHistory(action);
    },

    removeSolids: (ids, label) => {
        const removed = get().solids;
        const captured = ids.map((id) => removed[id]).filter(Boolean);
        if (captured.length === 0) return;
        const action: HistoryAction = {
            label,
            undo: () => set((s) => ({
                solids: { ...s.solids, ...Object.fromEntries(captured.map((g) => [g.id, g])) },
            })),
            redo: () => set((s) => {
                const solids = { ...s.solids };
                captured.forEach((g) => delete solids[g.id]);
                return { solids };
            }),
        };
        action.redo();
        get().pushHistory(action);
    },

    deleteSelected: (label) => {
        const state = get();
        const before = state.solids;
        const faceIdsBySolid = new Map<string, string[]>();
        const wholeSolidIds = new Set<string>();
        for (const id of state.selectedIds) {
            const parsed = parseFaceSelectionId(id);
            if (parsed && before[parsed.solidId]) {
                if (!before[parsed.solidId].faces.some((f) => f.id === parsed.faceId)) continue;
                const list = faceIdsBySolid.get(parsed.solidId) ?? [];
                list.push(parsed.faceId);
                faceIdsBySolid.set(parsed.solidId, list);
            } else if (before[id]) {
                wholeSolidIds.add(id);
            }
        }
        // Edge composites erase their bounding faces too (Sketch's eraser
        // coalesces the surfaces an edge separates).
        const edgeKeysBySolid = new Map<string, string[]>();
        for (const id of state.selectedIds) {
            const parsed = parseEdgeSelectionId(id);
            if (parsed && before[parsed.solidId]) {
                const list = edgeKeysBySolid.get(parsed.solidId) ?? [];
                if (!list.includes(parsed.edgeKey)) list.push(parsed.edgeKey);
                edgeKeysBySolid.set(parsed.solidId, list);
            }
        }
        edgeKeysBySolid.forEach((edgeKeys, solidId) => {
            if (wholeSolidIds.has(solidId)) return;
            const faceList = faceIdsBySolid.get(solidId) ?? [];
            facesOfEdges(before[solidId], edgeKeys).forEach((faceId) => {
                if (!faceList.includes(faceId)) faceList.push(faceId);
            });
            if (faceList.length > 0) faceIdsBySolid.set(solidId, faceList);
        });
        // Loose edges are first-class erase targets (Sketch's eraser on a
        // drawn segment): composite `looseEdge:<id>` entries die whole.
        const looseEdgeIds: string[] = [];
        for (const id of state.selectedIds) {
            const parsed = parseLooseEdgeSelectionId(id);
            if (parsed && state.edges[parsed]) looseEdgeIds.push(parsed);
        }
        if (faceIdsBySolid.size === 0 && wholeSolidIds.size === 0 && looseEdgeIds.length === 0) return false;

        const removedIds = [...wholeSolidIds];
        const updated: Record<string, SketchSolid> = {};
        faceIdsBySolid.forEach((faceIds, solidId) => {
            if (wholeSolidIds.has(solidId)) return;
            const solid = before[solidId];
            const remaining = solid.faces.filter((f) => !faceIds.includes(f.id));
            if (remaining.length === 0) removedIds.push(solidId);
            else updated[solidId] = { ...solid, faces: remaining };
        });

        const touchedIds = [...Object.keys(updated), ...removedIds];
        const removedEdges = Object.fromEntries(
            looseEdgeIds.map((id) => [id, state.edges[id]])
        );
        const action: HistoryAction = {
            label: label ?? 'Erase',
            undo: () => set((s) => ({
                solids: {
                    ...s.solids,
                    ...Object.fromEntries(touchedIds.map((id) => [id, before[id]])),
                },
                edges: { ...s.edges, ...removedEdges },
            })),
            redo: () => {
                set((s) => {
                    const solids = { ...s.solids, ...updated };
                    removedIds.forEach((id) => delete solids[id]);
                    return { solids };
                });
                if (looseEdgeIds.length > 0) {
                    set((s) => {
                        const edges = { ...s.edges };
                        looseEdgeIds.forEach((id) => delete edges[id]);
                        return { edges };
                    });
                }
            },
        };
        action.redo();
        get().pushHistory(action);
        get().select([]);
        return true;
    },

    addLooseSegment: (a, b, plane, label) => {
        const va = new THREE.Vector3(...a);
        const vb = new THREE.Vector3(...b);
        if (va.distanceTo(vb) < 1e-6) return null;
        // Exact duplicate (either winding) is idempotent.
        const duplicate = Object.values(get().edges).some((e) => {
            const ea = new THREE.Vector3(...e.a);
            const eb = new THREE.Vector3(...e.b);
            return (ea.distanceTo(va) < 1e-6 && eb.distanceTo(vb) < 1e-6)
                || (ea.distanceTo(vb) < 1e-6 && eb.distanceTo(va) < 1e-6);
        });
        if (duplicate) return null;

        const id = uuidv4();
        const beforeEdges = get().edges;
        const withNew: Record<string, SketchLooseEdge> = {
            ...beforeEdges,
            [id]: { id, a: [...a] as SketchVec3, b: [...b] as SketchVec3 },
        };
        const detected = detectClosedRegions(Object.values(withNew), plane);

        const newSolids: SketchSolid[] = [];
        let afterEdges = withNew;
        let formed: FormedFaceRef | null = null;
        if (detected) {
            const consumed = new Set(detected.consumedIds);
            afterEdges = Object.fromEntries(
                Object.entries(withNew).filter(([eid]) => !consumed.has(eid))
            );
            detected.faces.forEach((face) => {
                newSolids.push(createSketchSolid([face], 'Face'));
            });
            const firstFace = newSolids[0]?.faces[0];
            if (newSolids[0] && firstFace) formed = { solidId: newSolids[0].id, faceId: firstFace.id };
        }

        const action: HistoryAction = {
            label: label ?? (formed ? 'Draw Face' : 'Draw Line'),
            undo: () => set((s) => ({
                edges: beforeEdges,
                solids: Object.fromEntries(
                    Object.entries(s.solids).filter(([sid]) => !newSolids.some((n) => n.id === sid))
                ),
            })),
            redo: () => set((s) => ({
                edges: afterEdges,
                solids: { ...s.solids, ...Object.fromEntries(newSolids.map((n) => [n.id, n])) },
            })),
        };
        action.redo();
        get().pushHistory(action);
        return formed;
    },

    removeLooseEdges: (ids, label) => {
        const captured = ids.map((id) => get().edges[id]).filter(Boolean);
        if (captured.length === 0) return;
        const action: HistoryAction = {
            label: label ?? 'Erase Edge',
            undo: () => set((s) => ({
                edges: { ...s.edges, ...Object.fromEntries(captured.map((e) => [e.id, e])) },
            })),
            redo: () => set((s) => {
                const edges = { ...s.edges };
                captured.forEach((e) => delete edges[e.id]);
                return { edges };
            }),
        };
        action.redo();
        get().pushHistory(action);
    },

    transformLooseEdges: (ids, matrix, label) => {
        const captured = ids.map((id) => get().edges[id]).filter(Boolean);
        if (captured.length === 0) return;
        const movedOf = (e: SketchLooseEdge): SketchLooseEdge => ({
            id: e.id,
            a: new THREE.Vector3(...e.a).applyMatrix4(matrix).toArray() as SketchVec3,
            b: new THREE.Vector3(...e.b).applyMatrix4(matrix).toArray() as SketchVec3,
        });
        const action: HistoryAction = {
            label: label ?? 'Move Edge',
            undo: () => set((s) => ({
                edges: { ...s.edges, ...Object.fromEntries(captured.map((e) => [e.id, e])) },
            })),
            redo: () => set((s) => ({
                edges: { ...s.edges, ...Object.fromEntries(captured.map((e) => [e.id, movedOf(e)])) },
            })),
        };
        action.redo();
        get().pushHistory(action);
    },

    copySolids: (ids, matrix, label) => {
        const captured = ids.map((id) => get().solids[id]).filter(Boolean);
        return captured.length > 0 ? get().insertSolids(captured, matrix, label) : [];
    },

    stretchLooseEdges: (items, delta, label) => {
        const endsById = new Map<string, Set<'a' | 'b'>>();
        items.forEach((it) => endsById.set(it.id, new Set(it.ends)));
        const captured = [...endsById.keys()].map((id) => get().edges[id]).filter(Boolean);
        if (captured.length === 0) return;
        const movedOf = (e: SketchLooseEdge): SketchLooseEdge => {
            const ends = endsById.get(e.id);
            const next: SketchLooseEdge = {
                id: e.id,
                a: ends?.has('a')
                    ? [e.a[0] + delta.x, e.a[1] + delta.y, (e.a[2] ?? 0) + delta.z] as SketchVec3
                    : [...e.a] as SketchVec3,
                b: ends?.has('b')
                    ? [e.b[0] + delta.x, e.b[1] + delta.y, (e.b[2] ?? 0) + delta.z] as SketchVec3
                    : [...e.b] as SketchVec3,
            };
            return next;
        };
        const action: HistoryAction = {
            label: label ?? 'Move Edge',
            undo: () => set((s) => ({
                edges: { ...s.edges, ...Object.fromEntries(captured.map((e) => [e.id, e])) },
            })),
            redo: () => set((s) => ({
                edges: { ...s.edges, ...Object.fromEntries(captured.map((e) => [e.id, movedOf(e)])) },
            })),
        };
        action.redo();
        get().pushHistory(action);
    },

    copyLooseEdges: (ids, matrix, label) => {
        const captured = ids.map((id) => get().edges[id]).filter(Boolean);
        return captured.length > 0 ? get().insertLooseEdges(captured, matrix, label) : [];
    },

    insertSolids: (solids, matrix, label) => {
        if (solids.length === 0) return [];
        const clones = solids.map((solid) => transformSolid(copySolid(solid), matrix));
        const cloneIds = new Set(clones.map((c) => c.id));
        const action: HistoryAction = {
            label: label ?? 'Paste Solid',
            undo: () => set((s) => ({
                solids: Object.fromEntries(
                    Object.entries(s.solids).filter(([sid]) => !cloneIds.has(sid))
                ),
            })),
            redo: () => set((s) => ({
                solids: { ...s.solids, ...Object.fromEntries(clones.map((c) => [c.id, c])) },
            })),
        };
        action.redo();
        get().pushHistory(action);
        return clones.map((c) => c.id);
    },

    insertLooseEdges: (edges, matrix, label) => {
        if (edges.length === 0) return [];
        const clones = edges.map((e) => ({
            id: uuidv4(),
            a: new THREE.Vector3(...e.a).applyMatrix4(matrix).toArray() as SketchVec3,
            b: new THREE.Vector3(...e.b).applyMatrix4(matrix).toArray() as SketchVec3,
        }));
        const cloneIds = new Set(clones.map((c) => c.id));
        const action: HistoryAction = {
            label: label ?? 'Paste Edge',
            undo: () => set((s) => ({
                edges: Object.fromEntries(
                    Object.entries(s.edges).filter(([eid]) => !cloneIds.has(eid))
                ),
            })),
            redo: () => set((s) => ({
                edges: { ...s.edges, ...Object.fromEntries(clones.map((c) => [c.id, c])) },
            })),
        };
        action.redo();
        get().pushHistory(action);
        return clones.map((c) => c.id);
    },

    addGuide: (guide, label) => {
        const action: HistoryAction = {
            label: label ?? 'Add Guide',
            undo: () => set((s) => {
                const guides = { ...s.guides };
                delete guides[guide.id];
                return { guides };
            }),
            redo: () => set((s) => ({ guides: { ...s.guides, [guide.id]: guide } })),
        };
        action.redo();
        get().pushHistory(action);
    },

    removeGuides: (ids, label) => {
        const captured = ids.map((id) => get().guides[id]).filter(Boolean);
        if (captured.length === 0) return;
        const action: HistoryAction = {
            label: label ?? 'Erase Guides',
            undo: () => set((s) => ({
                guides: { ...s.guides, ...Object.fromEntries(captured.map((g) => [g.id, g])) },
            })),
            redo: () => set((s) => {
                const guides = { ...s.guides };
                captured.forEach((g) => delete guides[g.id]);
                return { guides };
            }),
        };
        action.redo();
        get().pushHistory(action);
    },

    setSolidMaterial: (solidIds, faceIds, materialId, label) => {
        const { solids } = get();
        const updates: Array<{ id: string; before: SketchSolid; after: SketchSolid }> = [];
        const faceSet = new Set(faceIds);
        for (const id of solidIds) {
            const solid = solids[id];
            if (!solid) continue;
            const faces = solid.faces.map((face) =>
                faceSet.has(face.id)
                    ? { ...face, materialId }
                    : (faceSet.size === 0 ? { ...face, materialId } : face)
            );
            // No face subset picked (Paint on a solid-level hit): repaint all faces.
            const effective = faceSet.size === 0
                ? solid.faces.map((f) => ({ ...f, materialId }))
                : faces;
            updates.push({ id, before: solid, after: { ...solid, faces: effective, materialId: faceSet.size === 0 ? materialId : solid.materialId } });
        }
        if (updates.length === 0) return;
        const action: HistoryAction = {
            label: label ?? 'Paint',
            undo: () => set((s) => ({
                solids: { ...s.solids, ...Object.fromEntries(updates.map((u) => [u.id, u.before])) },
            })),
            redo: () => set((s) => ({
                solids: { ...s.solids, ...Object.fromEntries(updates.map((u) => [u.id, u.after])) },
            })),
        };
        action.redo();
        get().pushHistory(action);
    },

    setSolidsVisible: (ids, visible, label) => {
        const updates: Array<{ id: string; before: SketchSolid; after: SketchSolid }> = [];
        for (const id of ids) {
            const solid = get().solids[id];
            if (!solid || solid.visible === visible) continue;
            updates.push({ id, before: solid, after: { ...solid, visible } });
        }
        if (updates.length === 0) return;
        const action: HistoryAction = {
            label: label ?? (visible ? 'Unhide' : 'Hide'),
            undo: () => set((s) => ({
                solids: { ...s.solids, ...Object.fromEntries(updates.map((u) => [u.id, u.before])) },
            })),
            redo: () => set((s) => ({
                solids: { ...s.solids, ...Object.fromEntries(updates.map((u) => [u.id, u.after])) },
            })),
        };
        action.redo();
        get().pushHistory(action);
    },

    addDimension: (dim, label) => {
        const record: SketchDimension = { ...dim, id: uuidv4() };
        const action: HistoryAction = {
            label: label ?? 'Dimension',
            undo: () => set((s) => {
                const dimensions = { ...s.dimensions };
                delete dimensions[record.id];
                return { dimensions };
            }),
            redo: () => set((s) => ({ dimensions: { ...s.dimensions, [record.id]: record } })),
        };
        action.redo();
        get().pushHistory(action);
        return record.id;
    },

    removeDimensions: (ids, label) => {
        const captured = ids.map((id) => get().dimensions[id]).filter(Boolean);
        if (captured.length === 0) return;
        const action: HistoryAction = {
            label: label ?? 'Delete Dimension',
            undo: () => set((s) => ({
                dimensions: { ...s.dimensions, ...Object.fromEntries(captured.map((d) => [d.id, d])) },
            })),
            redo: () => set((s) => {
                const dimensions = { ...s.dimensions };
                captured.forEach((d) => delete dimensions[d.id]);
                return { dimensions };
            }),
        };
        action.redo();
        get().pushHistory(action);
    },

    addLabel: (label, labelTag) => {
        const record: SketchLabel = { ...label, id: uuidv4() };
        const action: HistoryAction = {
            label: labelTag ?? 'Text',
            undo: () => set((s) => {
                const labels = { ...s.labels };
                delete labels[record.id];
                return { labels };
            }),
            redo: () => set((s) => ({ labels: { ...s.labels, [record.id]: record } })),
        };
        action.redo();
        get().pushHistory(action);
        return record.id;
    },

    removeLabels: (ids, label) => {
        const captured = ids.map((id) => get().labels[id]).filter(Boolean);
        if (captured.length === 0) return;
        const action: HistoryAction = {
            label: label ?? 'Delete Text',
            undo: () => set((s) => ({
                labels: { ...s.labels, ...Object.fromEntries(captured.map((l) => [l.id, l])) },
            })),
            redo: () => set((s) => {
                const labels = { ...s.labels };
                captured.forEach((l) => delete labels[l.id]);
                return { labels };
            }),
        };
        action.redo();
        get().pushHistory(action);
    },

    moveDimensions: (ids, delta, label) => {
        // The dim line repositions (offset moves); the measured points stay
        // pinned — the witness lines re-foot and the value keeps its meaning
        // (Sketch's dimension drag read).
        const captured = ids.map((id) => get().dimensions[id]).filter(Boolean);
        if (captured.length === 0) return;
        const movedOf = (d: SketchDimension): SketchDimension => ({
            ...d,
            offset: [d.offset[0] + delta.x, d.offset[1] + delta.y, d.offset[2] + delta.z] as SketchVec3,
        });
        const action: HistoryAction = {
            label: label ?? 'Move Dimension',
            undo: () => set((s) => ({
                dimensions: { ...s.dimensions, ...Object.fromEntries(captured.map((d) => [d.id, d])) },
            })),
            redo: () => set((s) => ({
                dimensions: { ...s.dimensions, ...Object.fromEntries(captured.map((d) => [d.id, movedOf(d)])) },
            })),
        };
        action.redo();
        get().pushHistory(action);
    },

    transformDimensions: (ids, matrix, label) => {
        // Whole-dimension transform: measured points AND the dim line move
        // together, so the annotation keeps its geometry wherever the
        // gesture came from (cross-workbench gumball / MOVE).
        const captured = ids.map((id) => get().dimensions[id]).filter(Boolean);
        if (captured.length === 0) return;
        const movedOf = (d: SketchDimension): SketchDimension => ({
            ...d,
            a: new THREE.Vector3(...d.a).applyMatrix4(matrix).toArray() as SketchVec3,
            b: new THREE.Vector3(...d.b).applyMatrix4(matrix).toArray() as SketchVec3,
            offset: new THREE.Vector3(...d.offset).applyMatrix4(matrix).toArray() as SketchVec3,
        });
        const action: HistoryAction = {
            label: label ?? 'Move Dimension',
            undo: () => set((s) => ({
                dimensions: { ...s.dimensions, ...Object.fromEntries(captured.map((d) => [d.id, d])) },
            })),
            redo: () => set((s) => ({
                dimensions: { ...s.dimensions, ...Object.fromEntries(captured.map((d) => [d.id, movedOf(d)])) },
            })),
        };
        action.redo();
        get().pushHistory(action);
    },

    moveLabels: (ids, delta, label) => {
        // The whole label rides (anchor + leader together) — the pick dot
        // sits on the anchor, so a drag must not strand the text behind.
        const captured = ids.map((id) => get().labels[id]).filter(Boolean);
        if (captured.length === 0) return;
        const movedOf = (l: SketchLabel): SketchLabel => ({
            ...l,
            anchor: [l.anchor[0] + delta.x, l.anchor[1] + delta.y, l.anchor[2] + delta.z] as SketchVec3,
            leader: [l.leader[0] + delta.x, l.leader[1] + delta.y, l.leader[2] + delta.z] as SketchVec3,
        });
        const action: HistoryAction = {
            label: label ?? 'Move Text',
            undo: () => set((s) => ({
                labels: { ...s.labels, ...Object.fromEntries(captured.map((l) => [l.id, l])) },
            })),
            redo: () => set((s) => ({
                labels: { ...s.labels, ...Object.fromEntries(captured.map((l) => [l.id, movedOf(l)])) },
            })),
        };
        action.redo();
        get().pushHistory(action);
    },

    transformLabels: (ids, matrix, label) => {
        // The whole label rides (anchor + leader together) through the
        // shared edit router's matrix.
        const captured = ids.map((id) => get().labels[id]).filter(Boolean);
        if (captured.length === 0) return;
        const movedOf = (l: SketchLabel): SketchLabel => ({
            ...l,
            anchor: new THREE.Vector3(...l.anchor).applyMatrix4(matrix).toArray() as SketchVec3,
            leader: new THREE.Vector3(...l.leader).applyMatrix4(matrix).toArray() as SketchVec3,
        });
        const action: HistoryAction = {
            label: label ?? 'Move Text',
            undo: () => set((s) => ({
                labels: { ...s.labels, ...Object.fromEntries(captured.map((l) => [l.id, l])) },
            })),
            redo: () => set((s) => ({
                labels: { ...s.labels, ...Object.fromEntries(captured.map((l) => [l.id, movedOf(l)])) },
            })),
        };
        action.redo();
        get().pushHistory(action);
    },

    setDrawingAxes: (axes, label) => {
        const before = get().drawingAxes;
        if (axes === null && before === null) return;
        const action: HistoryAction = {
            label: label ?? 'Place Axes',
            undo: () => set({ drawingAxes: before }),
            redo: () => set({ drawingAxes: axes }),
        };
        action.redo();
        get().pushHistory(action);
    },

    // Any non-plane selection retires the section-plane entity selection
    // (the two are exclusive — the gumball serves one target set).
    select: (ids) => set({ selectedIds: ids, sectionPlaneSelected: false }),
    setHovered: (id) => set({ hoveredId: id }),
    setVcbText: (text) => set({ vcbText: text }),
    setDisplayStyle: (style) => set({ displayStyle: style }),
    toggleGuides: () => set((s) => ({ showGuides: !s.showGuides })),
    setSelectedMaterial: (materialId) => set({ selectedMaterialId: materialId }),
    // Removing the plane retires its selection; a live update (gumball
    // commit) keeps the plane selected for the next gesture.
    setSectionPlane: (plane) =>
        set((s) => ({ sectionPlane: plane, sectionPlaneSelected: plane ? s.sectionPlaneSelected : false })),
    setSectionPlaneSelected: (on) => set({ sectionPlaneSelected: on }),
    setSectionBox: (box) => set({ sectionBox: box }),

    // --- groups & components ---
    groupSolids: (solidIds, name) => {
        const { solids } = get();
        const members = solidIds.filter((id) => solids[id]);
        if (members.length === 0) return null;
        const group: SketchGroup = {
            id: uuidv4(),
            name: name ?? `Group ${Object.keys(get().groups).length + 1}`,
            solidIds: members,
        };
        const before = members.map((id) => ({ id, groupId: solids[id].groupId }));
        const action: HistoryAction = {
            label: `Make ${group.name}`,
            undo: () => set((s) => ({
                groups: Object.fromEntries(Object.entries(s.groups).filter(([gid]) => gid !== group.id)),
                solids: Object.fromEntries(Object.entries(s.solids).map(([id, solid]) => [
                    id,
                    before.some((b) => b.id === id && b.groupId === undefined)
                        ? (({ groupId: _drop, ...rest }) => rest)(solid as SketchSolid & { groupId?: string })
                        : solid,
                ])),
            })),
            redo: () => set((s) => ({
                groups: { ...s.groups, [group.id]: group },
                solids: Object.fromEntries(Object.entries(s.solids).map(([id, solid]) => [
                    id,
                    members.includes(id) ? { ...solid, groupId: group.id } : solid,
                ])),
            })),
        };
        action.redo();
        get().pushHistory(action);
        return group.id;
    },

    ungroupSolids: (solidIds) => {
        const { solids, groups } = get();
        const affected = solidIds
            .map((id) => solids[id])
            .filter((s): s is SketchSolid => Boolean(s?.groupId));
        if (affected.length === 0) return;
        const captured = { groups: { ...groups }, solids: { ...solids } };
        const action: HistoryAction = {
            label: 'Ungroup',
            undo: () => set({ groups: captured.groups, solids: captured.solids }),
            redo: () => set((s) => {
                const nextGroups = { ...s.groups };
                const nextSolids = { ...s.solids };
                affected.forEach((solid) => {
                    if (nextSolids[solid.id]) nextSolids[solid.id] = { ...nextSolids[solid.id], groupId: undefined };
                });
                // Drop groups left empty by the ungroup.
                Object.values(nextGroups).forEach((g) => {
                    const remaining = g.solidIds.filter((id) => nextSolids[id]?.groupId === g.id);
                    if (remaining.length === 0) delete nextGroups[g.id];
                });
                return { groups: nextGroups, solids: nextSolids };
            }),
        };
        action.redo();
        get().pushHistory(action);
    },

    saveAsComponent: (solidIds, name) => {
        const { solids } = get();
        const members = solidIds.map((id) => solids[id]).filter(Boolean);
        if (members.length === 0) return null;
        // Multi-solid defs keep their relative placement; insert clones all.
        const faces = members.flatMap((s) => s.faces.map((f) => ({
            ...f,
            outer: f.outer.map((p) => [...p] as SketchVec3),
            holes: f.holes.map((h) => h.map((p) => [...p] as SketchVec3)),
        })));
        const def: SketchComponentDef = {
            id: uuidv4(),
            name: name ?? `Component ${Object.keys(get().componentDefs).length + 1}`,
            faces,
            materialId: members[0].materialId,
        };
        set((s) => ({ componentDefs: { ...s.componentDefs, [def.id]: def } }));
        return def.id;
    },

    insertComponent: (defId, at) => {
        const def = get().componentDefs[defId];
        if (!def) return null;
        // Translate the clone so the def's lowest Z rests on the placement point.
        let minZ = Infinity;
        def.faces.forEach((f) => f.outer.forEach((p) => { minZ = Math.min(minZ, p[2]); }));
        const translate = (p: SketchVec3): SketchVec3 =>
            at ? [p[0] + at[0], p[1] + at[1], p[2] + at[2] - minZ] : [...p] as SketchVec3;
        const faces: SketchFace[] = def.faces.map((f) => ({
            ...f,
            id: uuidv4(),
            origin: translate(f.origin),
            normal: [...f.normal] as SketchVec3,
            outer: f.outer.map(translate),
            holes: f.holes.map((h) => h.map(translate)),
        }));
        const solid: SketchSolid = {
            id: uuidv4(),
            name: def.name,
            faces,
            materialId: def.materialId,
            visible: true,
            locked: false,
        };
        get().addSolid(solid, `Insert ${def.name}`);
        return solid.id;
    },

    deleteComponentDef: (defId) => {
        set((s) => {
            const componentDefs = { ...s.componentDefs };
            delete componentDefs[defId];
            return { componentDefs };
        });
    },

    renameGroup: (groupId, name) => {
        set((s) => {
            const group = s.groups[groupId];
            if (!group) return {};
            return { groups: { ...s.groups, [groupId]: { ...group, name } } };
        });
    },

    toggleGroupVisibility: (groupId) => {
        const { groups, solids } = get();
        const group = groups[groupId];
        if (!group) return;
        const members = group.solidIds.map((id) => solids[id]).filter(Boolean);
        const anyVisible = members.some((s) => s.visible);
        const updates = members.map((s) => ({ id: s.id, next: { ...s, visible: !anyVisible } }));
        const action: HistoryAction = {
            label: `Toggle ${group.name}`,
            undo: () => set((s2) => ({
                solids: { ...s2.solids, ...Object.fromEntries(updates.map((u) => [u.id, solids[u.id]])) },
            })),
            redo: () => set((s2) => ({
                solids: { ...s2.solids, ...Object.fromEntries(updates.map((u) => [u.id, u.next])) },
            })),
        };
        action.redo();
        get().pushHistory(action);
    },

    // --- tags, scenes, shadows (panels) ---
    addTag: (name) => {
        const tag: SketchTag = {
            id: uuidv4(),
            name: name ?? `Tag ${Object.keys(get().tags).length + 1}`,
            visible: true,
        };
        const action: HistoryAction = {
            label: `Add ${tag.name}`,
            undo: () => set((s) => {
                const tags = { ...s.tags };
                delete tags[tag.id];
                return { tags };
            }),
            redo: () => set((s) => ({ tags: { ...s.tags, [tag.id]: tag } })),
        };
        action.redo();
        get().pushHistory(action);
        return tag.id;
    },

    removeTag: (tagId) => {
        const tag = get().tags[tagId];
        if (!tag) return;
        // Member solids lose the tag; capture their before-state for undo.
        const members = Object.values(get().solids).filter((s) => s.tagId === tagId);
        const action: HistoryAction = {
            label: `Delete ${tag.name}`,
            undo: () => set((s) => ({
                tags: { ...s.tags, [tag.id]: tag },
                solids: { ...s.solids, ...Object.fromEntries(members.map((m) => [m.id, m])) },
            })),
            redo: () => set((s) => {
                const tags = { ...s.tags };
                delete tags[tagId];
                const solids = { ...s.solids };
                members.forEach((m) => {
                    if (solids[m.id]) solids[m.id] = { ...solids[m.id], tagId: undefined };
                });
                return { tags, solids };
            }),
        };
        action.redo();
        get().pushHistory(action);
    },

    renameTag: (tagId, name) => {
        set((s) => {
            const tag = s.tags[tagId];
            if (!tag) return {};
            return { tags: { ...s.tags, [tagId]: { ...tag, name } } };
        });
    },

    setTagVisibility: (tagId, visible) => {
        const tag = get().tags[tagId];
        if (!tag) return;
        const action: HistoryAction = {
            label: `${visible ? 'Show' : 'Hide'} ${tag.name}`,
            undo: () => set((s) => (
                s.tags[tagId] ? { tags: { ...s.tags, [tagId]: { ...s.tags[tagId], visible: !visible } } } : {}
            )),
            redo: () => set((s) => (
                s.tags[tagId] ? { tags: { ...s.tags, [tagId]: { ...s.tags[tagId], visible } } } : {}
            )),
        };
        action.redo();
        get().pushHistory(action);
    },

    assignTag: (solidIds, tagId) => {
        const { solids } = get();
        const updates: Array<{ id: string; before: SketchSolid; after: SketchSolid }> = [];
        for (const id of solidIds) {
            const solid = solids[id];
            if (!solid || solid.tagId === tagId) continue;
            updates.push({ id, before: solid, after: { ...solid, tagId: tagId ?? undefined } });
        }
        if (updates.length === 0) return;
        const label = tagId
            ? `Tag ${updates.length} with ${get().tags[tagId]?.name ?? 'Tag'}`
            : 'Remove Tags';
        const action: HistoryAction = {
            label,
            undo: () => set((s) => ({
                solids: { ...s.solids, ...Object.fromEntries(updates.map((u) => [u.id, u.before])) },
            })),
            redo: () => set((s) => ({
                solids: { ...s.solids, ...Object.fromEntries(updates.map((u) => [u.id, u.after])) },
            })),
        };
        action.redo();
        get().pushHistory(action);
    },

    addScene: (camera, displayStyle, name) => {
        const scene: SketchScene = {
            id: uuidv4(),
            name: name ?? `Scene ${Object.keys(get().scenes).length + 1}`,
            camera: { ...camera },
            displayStyle,
        };
        set((s) => ({ scenes: { ...s.scenes, [scene.id]: scene } }));
        return scene.id;
    },

    updateScene: (id, camera, displayStyle) => {
        set((s) => {
            const scene = s.scenes[id];
            if (!scene) return {};
            return { scenes: { ...s.scenes, [id]: { ...scene, camera: { ...camera }, displayStyle } } };
        });
    },

    renameScene: (id, name) => {
        set((s) => {
            const scene = s.scenes[id];
            if (!scene) return {};
            return { scenes: { ...s.scenes, [id]: { ...scene, name } } };
        });
    },

    removeScene: (id) => {
        set((s) => {
            const scenes = { ...s.scenes };
            delete scenes[id];
            return { scenes };
        });
    },

    setShadows: (updates) => {
        set((s) => ({ shadows: { ...s.shadows, ...updates } }));
    },

    undo: () => {
        const { history, historyIndex } = get();
        if (historyIndex >= 0) {
            history[historyIndex].undo();
            set({ historyIndex: historyIndex - 1 });
        }
    },

    redo: () => {
        const { history, historyIndex } = get();
        if (historyIndex < history.length - 1) {
            history[historyIndex + 1].redo();
            set({ historyIndex: historyIndex + 1 });
        }
    },

    pushHistory: (action) => {
        const { history, historyIndex } = get();
        // Truncate the redo branch, then append (app-store history rule).
        const next = history.slice(0, historyIndex + 1);
        next.push(action);
        if (next.length > HISTORY_LIMIT) {
            next.shift();
        }
        set({ history: next, historyIndex: next.length - 1 });
    },

    clearDocument: () => set({
        solids: {},
        edges: {},
        guides: {},
        groups: {},
        componentDefs: {},
        tags: {},
        scenes: {},
        shadows: { ...DEFAULT_SHADOWS },
        dimensions: {},
        labels: {},
        drawingAxes: null,
        selectedIds: [],
        hoveredId: null,
        vcbText: '',
        sectionPlane: null, // session state: never leak into the next document
        sectionPlaneSelected: false,
        sectionBox: null,
        history: [],
        historyIndex: -1,
    }),

    hydrateSketchDocument: (payload) => {
        const solids = Object.fromEntries((payload.solids ?? []).map((s) => [s.id, s]));
        const edges = Object.fromEntries((payload.edges ?? []).map((e) => [e.id, e]));
        const guides = Object.fromEntries((payload.guides ?? []).map((g) => [g.id, g]));
        const groups = Object.fromEntries((payload.groups ?? []).map((g) => [g.id, g]));
        const componentDefs = Object.fromEntries((payload.componentDefs ?? []).map((c) => [c.id, c]));
        const tags = Object.fromEntries((payload.tags ?? []).map((t) => [t.id, t]));
        const scenes = Object.fromEntries((payload.scenes ?? []).map((sc) => [sc.id, sc]));
        const dimensions = Object.fromEntries((payload.dimensions ?? []).map((d) => [d.id, d]));
        const labels = Object.fromEntries((payload.labels ?? []).map((l) => [l.id, l]));
        set({
            solids,
            edges,
            guides,
            groups,
            componentDefs,
            tags,
            scenes,
            dimensions,
            labels,
            drawingAxes: payload.drawingAxes ?? null,
            shadows: { ...DEFAULT_SHADOWS, ...(payload.shadows ?? {}) },
            selectedIds: [],
            hoveredId: null,
            sectionPlane: null, // session state: never leak across documents
            sectionPlaneSelected: false,
            sectionBox: null,
            history: [],
            historyIndex: -1,
        });
    },

    sketchDocumentSnapshot: () => ({
        solids: Object.values(get().solids),
        edges: Object.values(get().edges),
        guides: Object.values(get().guides),
        groups: Object.values(get().groups),
        componentDefs: Object.values(get().componentDefs),
        tags: Object.values(get().tags),
        scenes: Object.values(get().scenes),
        shadows: get().shadows,
        dimensions: Object.values(get().dimensions),
        labels: Object.values(get().labels),
        drawingAxes: get().drawingAxes,
    }),
}));

/** New solid factory with the default palette material. */
export function createSketchSolid(faces: SketchSolid['faces'], name = 'Solid', materialId = 'concrete'): SketchSolid {
    return {
        id: uuidv4(),
        name,
        faces,
        materialId,
        visible: true,
        locked: false,
    };
}
